import React, { useState } from 'react';
import TrodoWordmark from 'features/onboarding/components/TrodoWordmark';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axiosInstance from 'shared/services/api/axiosInstance';
import teamService from 'shared/services/teamService';
import { useTeam } from 'shared/context/teamContext';
import WorkspaceSetupOverlay from 'features/onboarding/components/WorkspaceSetupOverlay';

const PANEL_BG = 'var(--surface-sunken)';
const ACCENT = 'var(--trodo-primary)';

const NewProjectPage = () => {
  const navigate = useNavigate();
  const { team: teamParam } = useParams();
  const { setSelectedTeam: setSelectedTeamContext } = useTeam();

  const [teamName, setTeamName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const backPath = teamParam
    ? `/${encodeURIComponent(decodeURIComponent(teamParam))}/chat`
    : '/dashboard';

  const handleBack = () => {
    navigate(backPath);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = teamName.trim();
    if (!name) {
      setError('Please enter a team name.');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const data = await teamService.createNewTeam(name);

      if (!data?.newTeam) {
        throw new Error('Team creation failed - no team returned');
      }

      const created = data.newTeam;
      const teamId = created._id || created.id;
      const refreshed = await axiosInstance.get(`/team/${teamId}`);
      const finalTeamData = {
        ...refreshed.data,
        role: 'owner',
        isOwner: true,
        isCreator: true
      };

      sessionStorage.removeItem('userTeams');
      localStorage.setItem('selectedTeam', finalTeamData.name);
      localStorage.setItem('selectedTeamData', JSON.stringify({
        ...finalTeamData,
        _lastFetched: Date.now()
      }));

      if (setSelectedTeamContext) {
        setSelectedTeamContext(finalTeamData);
      }

      window.dispatchEvent(new CustomEvent('teamDataUpdated', {
        detail: { teamData: finalTeamData }
      }));

      const encodedSlug = encodeURIComponent(finalTeamData.name);
      navigate(`/${encodedSlug}/chat`, { replace: true });
    } catch (err) {
      console.error('New project creation failed:', err);
      const message = err.response?.data?.message || err.message || 'Could not create team. Please try again.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const isValid = teamName.trim().length > 0;

  const inputClass =
    'w-full px-4 py-3 bg-surface border border-[color:var(--border-default)] rounded-lg text-[color:var(--text-primary)] placeholder-[color:var(--text-disabled)] focus:outline-none focus:ring-2 focus:ring-[color:rgb(var(--trodo-primary-rgb)/0.2)] focus:border-[color:var(--trodo-primary)] text-[15px]';

  return (
    <div
      className="min-h-screen w-full flex flex-col md:flex-row overflow-hidden"
      style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
    >
      <div
        className="flex flex-1 flex-col min-h-[100dvh] px-6 sm:px-10 lg:px-14 py-8 relative"
        style={{ backgroundColor: PANEL_BG }}
      >
        <Link to={backPath} className="inline-block w-fit shrink-0 mb-8 md:mb-12">
          <TrodoWordmark />
        </Link>

        <div className="flex flex-1 flex-col justify-center w-full max-w-md mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
          >
            <h1
              className="text-[2rem] sm:text-[2.25rem] leading-tight mb-3 text-[color:var(--text-primary)] text-left font-serif font-normal tracking-tight"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Create your team
            </h1>
            <p className="text-[15px] leading-relaxed text-[color:var(--text-muted)] mb-8">
              Choose a name for your workspace. You can invite teammates later from settings.
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="new-project-name" className="block text-sm font-medium text-[color:var(--text-secondary)] mb-2">
                  Team name
                </label>
                <input
                  type="text"
                  id="new-project-name"
                  value={teamName}
                  onChange={(e) => {
                    setTeamName(e.target.value);
                    setError('');
                  }}
                  className={inputClass}
                  placeholder="e.g. Acme Labs"
                  disabled={submitting}
                  autoComplete="organization"
                  autoFocus
                />
                <p className="text-xs text-[color:var(--text-muted)] mt-2">Must be unique across Trodo. Letters, numbers, and spaces are fine.</p>
              </div>

              {error && (
                <div className="p-3 rounded-lg text-sm bg-danger-soft text-danger border border-danger">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={!isValid || submitting}
                className={`w-full py-3.5 rounded-lg font-medium text-[15px] transition-colors ${
                  isValid && !submitting
                    ? 'text-ink-onBrand hover:opacity-[0.96]'
                    : 'bg-surface-hover text-[color:var(--text-muted)] cursor-not-allowed'
                }`}
                style={isValid && !submitting ? { backgroundColor: ACCENT } : undefined}
              >
                {submitting ? 'Creating workspace…' : 'Create workspace'}
              </button>

              <button
                type="button"
                onClick={handleBack}
                disabled={submitting}
                className={`w-full text-center text-sm text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] transition-colors py-1 ${
                  submitting ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                ← Back
              </button>
            </form>
          </motion.div>
        </div>

        <footer className="shrink-0 pt-10 pb-2 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-[color:var(--text-muted)]">
          <a href="https://docs.trodo.ai/" target="_blank" rel="noopener noreferrer" className="hover:text-[color:var(--text-primary)] transition-colors">
            Help
          </a>
          <a href="https://trodo.ai/privacy-policy" target="_blank" rel="noopener noreferrer" className="hover:text-[color:var(--text-primary)] transition-colors">
            Privacy
          </a>
          <a href="https://trodo.ai/terms-of-service" target="_blank" rel="noopener noreferrer" className="hover:text-[color:var(--text-primary)] transition-colors">
            Terms
          </a>
        </footer>
      </div>

      <div className="hidden md:block md:w-1/2 lg:w-[46%] min-h-[100dvh] relative bg-[color:var(--border-default)]">
        <img
          src={`${process.env.PUBLIC_URL || ''}/login-image.webp`}
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-center"
          decoding="async"
        />
      </div>

      {submitting && <WorkspaceSetupOverlay />}
    </div>
  );
};

export default NewProjectPage;
